import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Button, Modal, Badge, Spinner, EmptyState, PageHeader } from '../components/Common';
import { API_URL } from '../config';

const API = `${API_URL}/super-admin`;
const getToken = () => localStorage.getItem('bhamsa_sa_token');

const SuperAdminPlans = () => {
  const [plans,       setPlans]       = useState([]);
  const [admins,      setAdmins]      = useState([]);
  const [subs,        setSubs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showPlan,    setShowPlan]    = useState(false);
  const [showAssign,  setShowAssign]  = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [activeTab,   setActiveTab]   = useState('plans');

  const [planForm, setPlanForm] = useState({
    name: '', description: '', price: '', duration_days: 30,
  });
  const [assignForm, setAssignForm] = useState({
    user_id: '', plan_id: '', amount_paid: '',
  });

  const load = async () => {
    try {
      const [p, a, s] = await Promise.all([
        axios.get(`${API}/plans`,         { headers: { Authorization: `Bearer ${getToken()}` } }),
        axios.get(`${API}/admins`,        { headers: { Authorization: `Bearer ${getToken()}` } }),
        axios.get(`${API}/subscriptions`, { headers: { Authorization: `Bearer ${getToken()}` } }),
      ]);
      setPlans(p.data.plans);
      setAdmins(a.data.admins);
      setSubs(s.data.subscriptions);
    } catch { toast.error('Data load nahi hua'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSavePlan = async () => {
    if (!planForm.name || !planForm.price) {
      toast.error('Name aur Price required hai'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/plans`, planForm, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      toast.success('Plan ban gaya! ✅');
      setShowPlan(false);
      setPlanForm({ name: '', description: '', price: '', duration_days: 30 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    } finally { setSaving(false); }
  };

  const handleAssign = async () => {
    if (!assignForm.user_id || !assignForm.plan_id) {
      toast.error('Admin aur Plan select karo'); return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/assign-plan`, assignForm, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      toast.success(`✅ Plan assign ho gaya! Invoice: ${res.data.invoice_number}`);
      if (res.data.whatsapp_sent) toast.success('📱 WhatsApp bheja gaya!');
      if (res.data.email_sent)    toast.success('📧 Email bheja gaya!');
      setShowAssign(false);
      setAssignForm({ user_id: '', plan_id: '', amount_paid: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    } finally { setSaving(false); }
  };

  const pf = (k, v) => setPlanForm(p => ({ ...p, [k]: v }));
  const af = (k, v) => setAssignForm(p => ({ ...p, [k]: v }));

  // Auto fill price when plan selected
  const onPlanSelect = (planId) => {
    af('plan_id', planId);
    const plan = plans.find(p => String(p.id) === String(planId));
    if (plan) af('amount_paid', plan.price);
  };

  if (loading) return <Spinner />;

  return (
    <div className="page">
      <PageHeader
        title="📋 Plans & Billing"
        subtitle="Subscription plans aur billing manage karo"
        actions={
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="secondary" onClick={() => setShowPlan(true)}>+ New Plan</Button>
            <Button onClick={() => setShowAssign(true)}>💳 Plan Assign Karo</Button>
          </div>
        }
      />

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${activeTab === 'plans' ? 'active' : ''}`} onClick={() => setActiveTab('plans')}>📋 Plans ({plans.length})</button>
        <button className={`tab ${activeTab === 'subs' ? 'active' : ''}`} onClick={() => setActiveTab('subs')}>💳 Subscriptions ({subs.length})</button>
      </div>

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="card">
          <div className="table-wrap">
            {plans.length === 0 ? (
              <EmptyState icon="📋" title="Koi plans nahi" message="'+ New Plan' se banao" />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Plan Name</th><th>Price</th><th>Duration</th>
                    <th>Description</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map(p => (
                    <tr key={p.id}>
                      <td><strong>{p.name}</strong></td>
                      <td style={{ color: 'var(--accent2)', fontWeight: 700 }}>
                        ₹{parseFloat(p.price).toLocaleString('en-IN')}
                      </td>
                      <td>{p.duration_days} days</td>
                      <td style={{ color: 'var(--text2)', fontSize: 12 }}>
                        {p.description || '—'}
                      </td>
                      <td><Badge variant="green">Active</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Subscriptions Tab */}
      {activeTab === 'subs' && (
        <div className="card">
          <div className="table-wrap">
            {subs.length === 0 ? (
              <EmptyState icon="💳" title="Koi subscriptions nahi" message="Plan assign karo" />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Invoice</th><th>Shop</th><th>Plan</th>
                    <th>Amount</th><th>Start</th><th>End</th>
                    <th>WA</th><th>Email</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map(s => (
                    <tr key={s.id}>
                      <td style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 600 }}>
                        {s.invoice_number}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{s.user?.shop_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{s.user?.name}</div>
                      </td>
                      <td>{s.plan?.name}</td>
                      <td style={{ color: 'var(--accent2)', fontWeight: 700 }}>
                        ₹{parseFloat(s.amount_paid).toLocaleString('en-IN')}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>
                        {new Date(s.start_date).toLocaleDateString('en-IN')}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>
                        {new Date(s.end_date).toLocaleDateString('en-IN')}
                      </td>
                      <td>{s.whatsapp_sent ? <Badge variant="green">✅</Badge> : <Badge variant="gray">—</Badge>}</td>
                      <td>{s.email_sent ? <Badge variant="green">✅</Badge> : <Badge variant="gray">—</Badge>}</td>
                      <td>
                        <Badge variant={s.status === 'active' ? 'green' : 'red'}>
                          {s.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* New Plan Modal */}
      <Modal isOpen={showPlan} onClose={() => setShowPlan(false)} title="📋 New Plan Banao" size="sm">
        <div className="form-group">
          <label className="form-label">Plan Name *</label>
          <input className="form-input" placeholder="e.g. Monthly Plan" value={planForm.name} onChange={e => pf('name', e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Price (₹) *</label>
            <input className="form-input" type="number" placeholder="999" value={planForm.price} onChange={e => pf('price', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Duration (Days) *</label>
            <input className="form-input" type="number" placeholder="30" value={planForm.duration_days} onChange={e => pf('duration_days', e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <input className="form-input" placeholder="Plan description" value={planForm.description} onChange={e => pf('description', e.target.value)} />
        </div>
        <div className="modal-footer">
          <Button variant="ghost" onClick={() => setShowPlan(false)}>Cancel</Button>
          <Button onClick={handleSavePlan} loading={saving}>💾 Plan Banao</Button>
        </div>
      </Modal>

      {/* Assign Plan Modal */}
      <Modal isOpen={showAssign} onClose={() => setShowAssign(false)} title="💳 Plan Assign Karo" size="sm">
        <div className="form-group">
          <label className="form-label">Admin Select Karo *</label>
          <select className="form-input" value={assignForm.user_id} onChange={e => af('user_id', e.target.value)}>
            <option value="">-- Admin choose karo --</option>
            {admins.map(a => (
              <option key={a.id} value={a.id}>{a.shop_name} — {a.name}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Plan Select Karo *</label>
          <select className="form-input" value={assignForm.plan_id} onChange={e => onPlanSelect(e.target.value)}>
            <option value="">-- Plan choose karo --</option>
            {plans.map(p => (
              <option key={p.id} value={p.id}>{p.name} — ₹{p.price} ({p.duration_days} days)</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Amount (₹)</label>
          <input className="form-input" type="number" placeholder="Auto-fill hoga" value={assignForm.amount_paid} onChange={e => af('amount_paid', e.target.value)} />
        </div>
        <div className="alert alert-info">
          📱 WhatsApp + Email automatically jayega admin ko!
        </div>
        <div className="modal-footer">
          <Button variant="ghost" onClick={() => setShowAssign(false)}>Cancel</Button>
          <Button onClick={handleAssign} loading={saving}>💳 Plan Assign Karo</Button>
        </div>
      </Modal>
    </div>
  );
};

export default SuperAdminPlans;